import { Component, OnInit, OnChanges, Input, Output, EventEmitter, SimpleChanges, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CurrentStateService } from '@ddv/behaviors';
import { DropdownOption } from '@ddv/common-components';
import { getNQSHostForManageNamedQueriesLink } from '@ddv/http';
import { NamedQuery } from '@ddv/models';
import { Subscription } from 'rxjs';

import { NamedQueriesService } from '../../services/named-queries.service';

function sortByName(a: { name: string }, b: { name: string }): number {
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
}

@Component({
    selector: 'app-named-query-selector',
    templateUrl: './named-query-selector.component.html',
    styleUrls: ['./named-query-selector.component.scss'],
    standalone: false,
})
export class NamedQuerySelectorComponent implements OnInit, OnChanges, OnDestroy {
    @Input() namedQuery: NamedQuery | undefined;
    @Input() jumpedFrom: string | undefined;
    @Input() isReadOnly = false;
    @Input() enabled = true;

    @Output() namedQuerySelected = new EventEmitter<NamedQuery>();

    protected queryTypeList: DropdownOption[] = [{ text: 'ALL', key: 0, value: 0 }];
    protected queryType: DropdownOption = { text: 'ALL', key: 0, value: 0 };
    protected filteredNamedQueries: NamedQuery[] = [];
    protected routerLink: string | undefined;
    protected currentClient = '';
    protected preSelectedItem: NamedQuery | undefined;

    private clientCodeSubscription: Subscription | undefined;
    private namedQueries: NamedQuery[] = [];
    private priorNamedQuery: NamedQuery | undefined;

    constructor(
        private readonly cdr: ChangeDetectorRef,
        private readonly namedQueriesService: NamedQueriesService,
        private readonly currentStateService: CurrentStateService,
    ) {}

    ngOnInit(): void {
        if (this.enabled) {
            this.fetchNamedQueries();
        }

        this.clientCodeSubscription = this.currentStateService.clientCode$.subscribe({
            next: (currentClient) => {
                this.currentClient = currentClient;
                this.buildRouterLink(this.namedQuery?.id ?? 'new');
            },
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.namedQuery) {
            if (this.namedQuery?.id) {
                this.populateDataFromNQS(this.namedQuery.id);
            } else {
                this.buildRouterLink('new');
            }

            this.priorNamedQuery = this.namedQuery;
            this.preSelectedItem = changes.namedQuery.currentValue;

            const queryType = changes.namedQuery.currentValue?.normalizeQueryTypeName();
            this.selectQueryTypeFromDefinition(queryType ?? 'ALL');

            if (!changes.namedQuery.currentValue && changes.namedQuery.previousValue) {
                this.fetchNamedQueries();
            }
        }
    }

    resetSelector(): void {
        this.selectQueryTypeFromDefinition('ALL');
        this.filteredNamedQueries = [...this.namedQueries];
    }

    restorePriorSelection(): void {
        this.selectQueryTypeFromDefinition(getQueryTypeName(this.priorNamedQuery));
        this.preSelectedItem = undefined;
        this.cdr.detectChanges();
        this.preSelectedItem = this.priorNamedQuery;
    }

    ngOnDestroy(): void {
        this.clientCodeSubscription?.unsubscribe();
    }

    protected onQueryTypeChanged(): void {
        if (this.queryType.text === 'ALL') {
            this.filteredNamedQueries = [...this.namedQueries];
        } else {
            this.filteredNamedQueries = this.namedQueries.filter((ds) => ds.type.name === this.queryType.text);
        }
    }

    protected onNamedQuerySelected(item: NamedQuery): void {
        this.selectQueryTypeFromDefinition(getQueryTypeName(item));
        this.namedQuerySelected.emit(item);
        this.buildRouterLink(item.id ?? 'new');
    }

    private fetchNamedQueries(): void {
        this.namedQueriesService.fetchNamedQueries().subscribe({
            next: (namedQueries) => {
                this.namedQueries = namedQueries.sort(sortByName);
                this.initializeQueryTypeList();
            },
        });
    }

    private initializeQueryTypeList(): void {
        const queryTypeNames = new Set<string>();
        this.namedQueries.forEach((ds) => queryTypeNames.add(ds.type.name));
        this.filteredNamedQueries = this.namedQueries;
        this.queryTypeList = [
            ...this.queryTypeList,
            ...([...queryTypeNames].sort()).map((name, index) => ({ text: name, key: index + 1, value: index + 1 })),
        ];

        if (this.namedQuery) {
            this.selectQueryTypeFromDefinition(getQueryTypeName(this.priorNamedQuery));
        }
    }

    private selectQueryTypeFromDefinition(queryType: string): void {
        this.queryType = this.queryTypeList.find((qt) => qt.text === queryType) ?? { text: 'ALL', key: 0, value: 0 };
    }

    private buildRouterLink(id: string | number): void {
        const app = `https://${getNQSHostForManageNamedQueriesLink()}/#`;
        this.routerLink = `${app}/${this.currentClient}/queries/${id}`;
    }

    private populateDataFromNQS(id: string): void {
        this.namedQueriesService.fetchNamedQuery(id).subscribe({
            next: (namedQuery: NamedQuery | undefined) => {
                this.namedQuery!.name = namedQuery?.name ?? '';
                this.selectQueryTypeFromDefinition(namedQuery?.normalizeQueryTypeName() ?? '');
                this.buildRouterLink(namedQuery?.id ?? 'new');
            },
        });
    }
}

function getQueryTypeName(namedQuery: NamedQuery | undefined): string {
    return namedQuery?.normalizeQueryTypeName() ?? 'ALL';
}
